import * as React from 'react';

import { useDispatch } from 'react-redux';
import { loadOpenedSchemaModels } from '../../sagas/dataSaga/dataSagaActions';
import { useSelector } from 'react-redux';
import { getIsLoadingOpenedSchemaModels, getOpenedSchemaModels } from '../../reducers/data/dataReducer';
import { Box, Button, Divider, List, ListItem, ListItemButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { openTrainedModelModalAction } from '../../sagas/trainedModelSaga/trainedModelSagaActions';
import { BadgeBoxSx, FlexBoxColumn, FlexBoxInline, PrimaryBtnSx, TooltipSlotProps } from '../../@shared/templates';
import { Palette, Sizes } from '../../@shared/palette';
import { useBadge } from '../../hooks/useBadge';
import SchemaModelsShimmer from './SchemaModelsShimmer';
import { getGuideStep, setGuideStep } from '../../reducers/main/mainReducer';
import GuideCallout from '../../components/GuideCallout';
import { GuideStepEnum } from '../../reducers/main/mainModel';
import { useNavigate } from 'react-router-dom';


type SchemaModelsProps = {
  id: string;
  name: string;
}

const SchemaModels = (props: SchemaModelsProps) => {
  const dispatch = useDispatch();
  const models = useSelector(getOpenedSchemaModels);
  const isLoadingModels = useSelector(getIsLoadingOpenedSchemaModels);
  const navigate = useNavigate();
  const { getBadgeColor } = useBadge();
  const guideStep = useSelector(getGuideStep);

  const onAddNewModel = React.useCallback(() => {
    if (props.id) {
      dispatch(openTrainedModelModalAction(props.id))
    }

    if(guideStep !== GuideStepEnum.None) {
      dispatch(setGuideStep(GuideStepEnum.FillModelModal));
    }
  }, [dispatch, guideStep, props.id]);

  const openModel = React.useCallback((name: string) => {
    navigate(`/${props.name}/${name}`)
  }, [navigate, props.name]);

  React.useEffect(() => {
    if (props.id) {
      dispatch(loadOpenedSchemaModels(props.id))
    }
  }, [dispatch, props.id])

  const listOfModels = React.useMemo(() => {

    if (isLoadingModels) {
      return <SchemaModelsShimmer />
    }

    return <List sx={{ width: '100%', padding: "20px 0" }}>
      {models?.map((x, i) => {
        const typeColor = getBadgeColor(x.modelTypeId);
        const stateColor = getBadgeColor(x.modelStateId);
        const queueColor = getBadgeColor(x.queueStateId);

        return <Box key={x.id}>
          <ListItem sx={{ padding: 0 }}>
            <Tooltip placement="bottom" open={i === 0 && guideStep === GuideStepEnum.OpenCreatedModel} slotProps={TooltipSlotProps} title={
              <GuideCallout>
                Click on this model to open it and view its details, training status, and metrics.
              </GuideCallout>
            } arrow>
              <ListItemButton onClick={() => openModel(x.name)}>
                <Box>
                <Box sx={{ ...FlexBoxInline, gap: "10px", alignItems: "flex-start", marginBottom: "5px" }}>
                  <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>{x.name}</Typography>
                </Box>
                {x.description && <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary, marginBottom: "5px" }}>{x.description}</Typography>}
                <Box sx={{ ...FlexBoxInline, fontSize: Sizes.pageSecondTitle }}>
                  <Box sx={{ ...BadgeBoxSx, background: typeColor.backgroundColor }}>
                    <Typography sx={{ fontSize: Sizes.textSecondary, color: typeColor.textColor }}>{x.modelTypeId.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}</Typography>
                  </Box>
                  <Box sx={{ ...BadgeBoxSx, background: stateColor.backgroundColor }}>
                    <Typography sx={{ fontSize: Sizes.textSecondary, color: stateColor.textColor }}>{x.modelStateId.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}</Typography>
                  </Box>
                  <Box sx={{ ...BadgeBoxSx, background: queueColor.backgroundColor }}>
                    <Typography sx={{ fontSize: Sizes.textSecondary, color: queueColor.textColor }}>{x.queueStateId.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}</Typography>
                  </Box>
                </Box>
              </Box>
            </ListItemButton>
            </Tooltip>
          </ListItem>
          {i !== models.length - 1 && <Divider variant="middle" component="li" />}
        </Box>
      })}
    </List>

  }, [getBadgeColor, guideStep, isLoadingModels, models, openModel]);

  return <Box sx={{ ...FlexBoxColumn, gap: 0 }}>
    <Tooltip placement="bottom" open={guideStep === GuideStepEnum.ClickAddModelBtn} slotProps={TooltipSlotProps} title={
      <GuideCallout>
        Click to add a new model to your schema. You can create multiple models for the same schema, each with different algorithms or settings, and compare their performance to choose the best one for your use case.
      </GuideCallout>
    } arrow>
      <Button sx={PrimaryBtnSx} onClick={onAddNewModel} startIcon={<AddIcon />}>New Model</Button>
    </Tooltip>
    {listOfModels}
  </Box>

}

export default SchemaModels;
